<template>
  <MountingPortal
    mount-to="body"
    append
  >
    <transition
      enter-active-class="tw-transition tw-duration-200 tw-ease-out"
      enter-class="tw-opacity-0"
      enter-to-class="tw-opacity-100"
      leave-active-class="tw-transition tw-duration-150 tw-ease-in"
      leave-class="tw-opacity-100"
      leave-to-class="tw-opacity-0"
    >
      <div
        v-if="value"
        class="tw-fixed tw-inset-0 tw-z-[1050] tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-bg-black/40 tw-p-4"
        role="dialog"
        aria-modal="true"
        @click.self="onBackdrop"
      >
        <div
          class="tw-relative tw-flex tw-w-full tw-flex-col tw-rounded-2xl tw-bg-white tw-p-6"
          :class="panelClass"
        >
          <slot name="header"></slot>
          <slot></slot>
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>
  </MountingPortal>
</template>

<script>
import { MountingPortal } from 'portal-vue';

/**
 * AppModal — Tailwind-native modal shell.
 *
 * Content-agnostic: backdrop + panel + behaviors only. Callers fill the
 * `header`, default, and `footer` slots. Teleported to <body> so it escapes
 * any parent stacking context.
 *
 * Usage:
 *   <AppModal v-model="isOpen" panel-class="tw-max-w-3xl" @close="onClose">
 *     <template #header>...</template>
 *     ...body...
 *     <template #footer>...</template>
 *   </AppModal>
 */
export default {
  name: 'AppModal',

  components: {
    MountingPortal,
  },

  props: {
    // v-model: open state.
    value: {
      type: Boolean,
      default: false,
    },
    // Extra classes for the panel (e.g. width: `tw-max-w-3xl`).
    panelClass: {
      type: String,
      default: '',
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    closeOnEsc: {
      type: Boolean,
      default: true,
    },
    lockScroll: {
      type: Boolean,
      default: true,
    },
  },

  watch: {
    value: {
      handler(isOpen) {
        if (isOpen) {
          this.addEscListener();
          this.applyScrollLock();
        } else {
          this.removeEscListener();
          this.releaseScrollLock();
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.removeEscListener();
    this.releaseScrollLock();
  },

  methods: {
    onBackdrop() {
      if (this.closeOnBackdrop) this.emitClose();
    },
    emitClose() {
      this.$emit('input', false);
      this.$emit('close');
    },
    handleKeydown(event) {
      if (this.closeOnEsc && event.key === 'Escape') this.emitClose();
    },
    addEscListener() {
      document.addEventListener('keydown', this.handleKeydown);
    },
    removeEscListener() {
      document.removeEventListener('keydown', this.handleKeydown);
    },
    applyScrollLock() {
      if (this.lockScroll) document.body.style.overflow = 'hidden';
    },
    releaseScrollLock() {
      if (this.lockScroll) document.body.style.overflow = '';
    },
  },
};
</script>
